import * as React from 'react'

import { Box, Icon, Section, Spinner } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import hasFeature from 'modules/core/lib/hasFeature'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import BalanceCarryoverForm from 'modules/attendance/lib/forms/BalanceCarryoverForm'
import timeoffCompensations, {
  TimeoffCompensationsProvider,
} from 'modules/attendance/store/collections/timeoffCompensation'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import BalanceCarryover from 'modules/attendance/store/models/BalanceCarryover'
import { RESET_FREQUENCIES } from 'modules/attendance/store/models/BalanceCarryover'

import FormComponent from './FormComponent'

interface PolicyProps {
  attendanceSetting: AttendanceSetting
  balanceCarryover: BalanceCarryover
  setBalanceCarryover: (balanceCarryover: BalanceCarryover) => void
  hasBankOfHours: boolean
}

const PolicyBalance = ({
  attendanceSetting,
  balanceCarryover,
  setBalanceCarryover,
  hasBankOfHours,
}: PolicyProps) => {
  const balanceCarryoverId = attendanceSetting.attributes.balance_carryover_id
  const employeesSettingId = +attendanceSetting.attributes.id
  const hasWeeklyBalanceCompensation = hasFeature('dev_time_tracking_weekly_balance_compensation')

  const { loading } = useAsyncData(
    () => (!balanceCarryover.isNew ? balanceCarryover.fetch() : Promise.resolve()),
    [balanceCarryover.attributes.id]
  )

  let attributes = {}

  if (hasWeeklyBalanceCompensation) {
    const resetFrecuency = balanceCarryover.resetFrequency
    const frecuencyNumber =
      balanceCarryover.attributes.frequency_number ??
      (resetFrecuency === RESET_FREQUENCIES.NEVER ? undefined : 1)

    attributes = {
      id: balanceCarryover.attributes.id,
      effective_at: balanceCarryover.attributes.effective_at,
      attendance_employees_setting_id: balanceCarryover.attributes.attendance_employees_setting_id,
      reset_frequency: resetFrecuency,
      frequency_number: frecuencyNumber,
    }
  } else {
    attributes = {
      id: balanceCarryover.attributes.id,
      effective_at: balanceCarryover.attributes.effective_at,
      attendance_employees_setting_id: balanceCarryover.attributes.attendance_employees_setting_id,
      reset_frequency: balanceCarryover.attributes.reset_frequency,
      frequency_number: balanceCarryover.attributes.frequency_number,
    }
  }

  const form = new BalanceCarryoverForm(attributes)

  const helpCenterLink = {
    href: helpCenterUrl('time_tracking'),
    label: i18n.t('time_tracking.settings.balance.description_link'),
  }

  useEffect(() => {
    if (loading) return

    setBalanceCarryover(
      new BalanceCarryover({
        id: balanceCarryoverId,
        attendance_employees_setting_id: employeesSettingId,
      })
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceCarryoverId, employeesSettingId])

  const sectionData: {
    illustration: React.ComponentProps<typeof Icon.Large>['icon']
    title: string
    description: string
    link?: { href: string; label: string }
  } = {
    illustration: 'tracking-timer',
    title: i18n.t('time_tracking.settings.balance.title'),
    description: i18n.t('time_tracking.settings.balance.description'),
    link: helpCenterLink,
  }

  if (hasBankOfHours) {
    sectionData.illustration = 'balance'
    sectionData.title = i18n.t('time_tracking.settings.bank_of_hours.title')
    sectionData.description = i18n.t('time_tracking.settings.bank_of_hours.description')
    sectionData.link = undefined
  }

  return (
    <Section
      illustration={sectionData.illustration}
      title={sectionData.title}
      description={sectionData.description}
      link={sectionData.link}
    >
      <Box gap="s24">
        {loading && <Spinner />}
        {!loading && (
          <TimeoffCompensationsProvider collection={timeoffCompensations}>
            <FormComponent
              attendanceSetting={attendanceSetting}
              balanceCarryover={balanceCarryover}
              form={form}
              hasBankOfHours={hasBankOfHours}
            />
          </TimeoffCompensationsProvider>
        )}
      </Box>
    </Section>
  )
}

// Keep type reference to avoid orphan checks
// FormComponent is still used in timeoff module
export default observer(PolicyBalance)
